/**
 * The agent's thinking, as a comic panel about the components it is touching.
 *
 * Deliberately not a spinner and not a transcript. It narrates the SURFACE —
 * "reading the Template picker", "approving 3 rows" — because the operator is
 * looking at those controls while it happens, and a line that names the thing
 * under their cursor is the difference between a status and an explanation.
 *
 * Renders in two sizes so the same words appear whether the bubble is collapsed
 * or open. The collapsed form is what someone sees when they are not looking
 * for it, so it carries the same sentence rather than a truncated hint.
 */
import { isAgentWorking, useAgentActivity } from '../agentActivity';
import { cn } from '@/lib/utils';

interface AgentThinkingComicProps {
  readonly variant: 'collapsed' | 'expanded';
  readonly className?: string;
}

export function AgentThinkingComic({ variant, className }: AgentThinkingComicProps) {
  const phase = useAgentActivity((s) => s.phase);
  const line = useAgentActivity((s) => s.line);
  const targets = useAgentActivity((s) => s.targets);
  const touchables = useAgentActivity((s) => s.touchables);

  const working = isAgentWorking(phase);
  // Settled and refused both still have something worth saying; idle does not.
  if (phase === 'idle' || !line) return null;

  const named = targets
    .map((key) => touchables[key]?.label)
    .filter((label): label is string => Boolean(label));

  const collapsed = variant === 'collapsed';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'agent-comic pointer-events-none relative rounded-xl border bg-paper shadow-md',
        collapsed ? 'max-w-[15rem] px-3 py-2' : 'w-full px-3 py-2.5',
        phase === 'refused' ? 'border-destructive/40' : 'border-teal/35',
        className,
      )}
    >
      <span className="flex items-start gap-2">
        <span
          className={cn(
            'agent-comic-orb mt-0.5 shrink-0 rounded-full',
            collapsed ? 'size-2.5' : 'size-3',
            phase === 'refused' ? 'bg-destructive' : 'bg-teal',
            working && 'agent-comic-orb-live',
          )}
          aria-hidden="true"
        />
        <span className="min-w-0">
          <span
            className={cn(
              'block leading-snug text-ink',
              collapsed ? 'text-[12px]' : 'text-[13px]',
            )}
          >
            {line}
          </span>
          {named.length > 0 && (
            <span className="mt-0.5 block truncate text-[11px] leading-snug text-ink-3">
              on {named.join(', ')}
            </span>
          )}
        </span>
      </span>
      {/* The tail. Points at the bubble when collapsed, at the thread when open. */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute size-2.5 rotate-45 border-b border-r bg-paper',
          phase === 'refused' ? 'border-destructive/40' : 'border-teal/35',
          collapsed ? '-bottom-[6px] right-6' : '-bottom-[6px] left-6',
        )}
      />
    </div>
  );
}
